import DashboardLayout from "@/layouts/patrimoine/DashboardLayout.vue";
import UserProfile from "@/views/shared/UserProfile.vue";
import ConseillerUser from "@/views/shared/ConseillerUser.vue";
import MonAnalyse from "@/views/patrimoine/client/MonAnalyse.vue";
import ProfilRisque from "@/views/patrimoine/client/ProfilRisque.vue";
import AnalyseSup from "@/views/patrimoine/client/AnalyseSup.vue";
import MesRecommandations from "@/views/patrimoine/client/MesRecommandations.vue";
import MesActions from "@/views/patrimoine/client/MesActions.vue";
import Aide from "@/views/patrimoine/client/Aide.vue";
import SummRy from "@/layouts/patrimoine/SummRy.vue";
import GesUserPage from "@/views/shared/admin/GesUserPage.vue";
import Dashboard from "@/layouts/patrimoine/Dashboard.vue";
import PaymentPage from "@/layouts/patrimoine/PaymentPage.vue";

import { ROLES, STAFF_ROLES, redirectAnalysePatrimonialeRoot } from "./constants";
import { PERMISSIONS } from "@/utils/permissions";

export const module1Routes = [
  /**
   * Tunnel de paiement : hors DashboardLayout, donc sans sidebar ni en-tête.
   * L'URL reste sous /analyse-patrimoniale (liens e-mail et retours LKM existants),
   * mais la page s'affiche seule pour ne pas disperser l'attention.
   */
  {
    path: "/analyse-patrimoniale/page-de-paiement",
    name: "Paiement",
    component: PaymentPage,
    meta: { requiresAuth: true, title: "Paiement", robots: "noindex, nofollow" },
  },
  {
    path: "/analyse-patrimoniale",
    component: DashboardLayout,
    redirect: redirectAnalysePatrimonialeRoot,
    meta: { requiresAuth: true },
    children: [
      { path: "dashboards", name: "Tableau de bord", component: Dashboard },
      { path: "profil", name: "Profil utilisateur", component: UserProfile },
      { path: "contact", name: "Parlez à un conseiller", component: ConseillerUser },
      { path: "mon-analyse", name: "Mon analyse patrimoniale", component: MonAnalyse },
      {
        path: "profil-risque",
        name: "Profil de risque investisseur",
        component: ProfilRisque,
        meta: { requiresRole: [ROLES.USER, "client"] },
      },
      { path: "analyses-sups", name: "Analyses approfondies", component: AnalyseSup },
      {
        path: "mes-recommandations",
        name: "Mes recommandations",
        component: MesRecommandations,
        meta: { requiresRole: [ROLES.USER, "client"] },
      },
      {
        path: "mes-actions",
        name: "Mes actions",
        component: MesActions,
        meta: { requiresRole: [ROLES.USER, "client"] },
      },
      {
        path: "aide",
        name: "Aide",
        component: Aide,
        meta: { requiresRole: [ROLES.USER, "client"] },
      },
      {
        path: "statistiques",
        name: "Statistiques",
        component: SummRy,
        meta: {
          requiresAnyPermission: [PERMISSIONS.GESTION_UTILISATEURS],
          requiresRole: [ROLES.ADMIN, ROLES.EMPLOYE],
        },
      },
      {
        path: "entrer-en-relation",
        redirect: "/analyse-patrimoniale/dashboards",
      },
      {
        path: "gestion-signatures",
        name: "Vérification justificatifs",
        component: () => import("@/views/patrimoine/admin/GestionSignatures.vue"),
        meta: { requiresAnyPermission: [PERMISSIONS.CONFORMITE], requiresRole: STAFF_ROLES },
      },
      {
        // Pas de requiresAnyPermission : ce mécanisme ne regarde que Employe.permissions,
        // jamais role_employe, et un compte IT réel n'a que role_employe="it" (voir
        // canAccessItDiagnostic() dans permissions.js, vérifié en dur dans le composant).
        path: "diagnostic-it",
        name: "Diagnostic IT",
        component: () => import("@/views/patrimoine/admin/DiagnosticIT.vue"),
        meta: { requiresRole: STAFF_ROLES },
      },
      {
        // Route dédiée pour l'IT : seul moyen d'uploader/gérer les modèles PDF (CGU,
        // contrat de souscription) sans donner accès aux autres onglets de
        // "Gestion des signatures" (dont les pièces d'identité clients, réservées
        // conformite). Même composant que l'onglet Modèles PDF, qui se garde lui-même
        // via canManageSignaturePdfTemplates(). Voir aussi IT_ALLOWED_PATHS (routes.js)
        // et l'entrée de menu correspondante (sidebarProfileMenus.js).
        path: "modeles-pdf",
        name: "Modèles PDF",
        component: () => import("@/views/patrimoine/admin/GestionSignaturesTemplatesTab.vue"),
        meta: { requiresRole: STAFF_ROLES },
      },
      {
        path: "validation-clients-kyc",
        name: "Confirmation Paiement",
        component: () => import("@/views/patrimoine/admin/ValidationClientsKYC.vue"),
        meta: { requiresAnyPermission: [PERMISSIONS.CONFORMITE], requiresRole: STAFF_ROLES },
      },
      {
        path: "gestion-namescan",
        name: "NameScan PEP & Sanctions",
        component: () => import("@/views/patrimoine/admin/GestionNameScan.vue"),
        meta: { requiresAnyPermission: [PERMISSIONS.CONFORMITE], requiresRole: STAFF_ROLES },
      },
      {
        path: "statut-paiement-malia",
        name: "Statut paiement",
        component: () => import("@/views/patrimoine/admin/PaiementStatutMalia.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.CONFORMITE,
            PERMISSIONS.GESTION_UTILISATEURS,
          ],
          requiresRole: STAFF_ROLES,
        },
      },
      {
        path: "message-clients",
        name: "Alertes clients",
        redirect: "/analyse-patrimoniale/requetes-clients",
        meta: { requiresRole: ROLES.ADMIN },
      },
      {
        path: "requetes-clients",
        name: "Requêtes clients",
        component: () => import("@/views/patrimoine/admin/ContactClient.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.VOIR,
            PERMISSIONS.LECTURE_SEULE_GLOBAL,
            PERMISSIONS.VALIDER,
          ],
          requiresRole: STAFF_ROLES,
        },
      },
      {
        path: "gestion-clients",
        name: "Dossiers clients",
        component: () => import("@/views/patrimoine/admin/GestionClients.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.VOIR,
            PERMISSIONS.LECTURE_SEULE_GLOBAL,
            PERMISSIONS.VALIDER,
            PERMISSIONS.GESTION_UTILISATEURS,
          ],
          requiresRole: STAFF_ROLES,
        },
      },
      {
        path: "gestion-formulaires",
        name: "État des formulaires",
        component: () => import("@/views/patrimoine/admin/GestionFormulaires.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.VOIR,
            PERMISSIONS.LECTURE_SEULE_GLOBAL,
            PERMISSIONS.VALIDER,
            PERMISSIONS.GESTION_UTILISATEURS,
          ],
          requiresRole: STAFF_ROLES,
        },
      },
      {
        path: "gestion-clients",
        name: "Dossiers clients",
        component: () => import("@/views/patrimoine/admin/GestionClients.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.VOIR,
            PERMISSIONS.LECTURE_SEULE_GLOBAL,
            PERMISSIONS.VALIDER,
            PERMISSIONS.GESTION_UTILISATEURS,
          ],
          requiresRole: ROLES.ADMIN,
        },
      },
      {
        path: "gestion-formulaires",
        name: "État des formulaires",
        component: () => import("@/views/patrimoine/admin/GestionFormulaires.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.VOIR,
            PERMISSIONS.LECTURE_SEULE_GLOBAL,
            PERMISSIONS.VALIDER,
            PERMISSIONS.GESTION_UTILISATEURS,
          ],
          requiresRole: ROLES.ADMIN,
        },
      },
      {
        path: "val-analyses",
        name: "Validation et Génération des analyses",
        component: () => import("@/views/patrimoine/admin/AnalysePat.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.VOIR,
            PERMISSIONS.LECTURE_SEULE_GLOBAL,
            PERMISSIONS.VALIDER,
          ],
          requiresRole: STAFF_ROLES,
        },
      },
      {
        path: "suivi-analyses",
        name: "Suivi des analyses",
        component: () => import("@/views/patrimoine/employe/SuiviAnalyseConseiller.vue"),
        meta: {
          requiresAnyPermission: [
            PERMISSIONS.VOIR,
            PERMISSIONS.LECTURE_SEULE_GLOBAL,
            PERMISSIONS.VALIDER,
          ],
          requiresRole: STAFF_ROLES,
        },
      },
      {
        path: "gestion-utilisateur",
        name: "Gestion des utilisateurs",
        component: GesUserPage,
        meta: {
          requiresAnyPermission: [PERMISSIONS.GESTION_UTILISATEURS],
          requiresRole: [ROLES.ADMIN, ROLES.EMPLOYE],
        },
      },
      {
        path: "bilan/:id",
        name: "Rapport Patrimonial",
        component: () => import("@/views/patrimoine/client/BilanPatPage.vue"),
      },
      {
        path: "bilan-duo",
        name: "Rapport Patrimonial Duo",
        component: () => import("@/views/patrimoine/client/BilanPatDuoPage.vue"),
      },
    ],
  },
];
